// File: src/pages/TabNotification.js

import React, { useState, useEffect } from 'react';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '../../hooks/useNotificationPreferences';

function NotificationTab() {
    const { data: preferences, isLoading, error } = useNotificationPreferences();
    const updatePreferences = useUpdateNotificationPreferences();

    const [localPreferences, setLocalPreferences] = useState({
        info: true,
        alert: true,
        subscription: true,
        events: true
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    // Update local state when preferences are loaded
    useEffect(() => {
        if (preferences) {
            setLocalPreferences(preferences);
        }
    }, [preferences]);

    const handleToggle = (key) => {
        setLocalPreferences(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage('');

        try {
            await updatePreferences.mutateAsync(localPreferences);
            setSaveMessage('Preferences saved successfully!');
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (error) {
            setSaveMessage('Failed to save preferences. Please try again.');
            console.error('Error saving preferences:', error);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card">
                <div className="card-body text-center text-danger">
                    Failed to load notification preferences. Please refresh the page.
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="card">
                {/* Card header START */}
                <div className="card-header border-0 pb-0">
                    <h5 className="card-title">Email Notifications</h5>
                    <p className="mb-0">
                        Choose which types of notifications you'd like to receive via email.
                        You can change these settings at any time.
                    </p>
                </div>
                {/* Card header END */}

                {/* Card body START */}
                <div className="card-body pb-0">
                    {/* Notification START */}
                    <ul className="list-group list-group-flush">
                        {/* Alerts */}
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                            <div className="me-2">
                                <h6 className="mb-0">Alerts</h6>
                                <p className="small mb-0">
                                    Important time-sensitive notifications like game cancellations,
                                    schedule changes, and urgent team updates.
                                </p>
                            </div>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="alertSwitch"
                                    checked={localPreferences.alert}
                                    onChange={() => handleToggle('alert')}
                                />
                            </div>
                        </li>

                        {/* Information */}
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                            <div className="me-2">
                                <h6 className="mb-0">Information</h6>
                                <p className="small mb-0">
                                    General updates about your account, league announcements,
                                    and helpful tips about the platform.
                                </p>
                            </div>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="infoSwitch"
                                    checked={localPreferences.info}
                                    onChange={() => handleToggle('info')}
                                />
                            </div>
                        </li>

                        {/* Subscriptions */}
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                            <div className="me-2">
                                <h6 className="mb-0">Subscriptions</h6>
                                <p className="small mb-0">
                                    Updates about your league memberships, season registrations,
                                    and subscription-related information.
                                </p>
                            </div>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="subscriptionSwitch"
                                    checked={localPreferences.subscription}
                                    onChange={() => handleToggle('subscription')}
                                />
                            </div>
                        </li>

                        {/* Events */}
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                            <div className="me-2">
                                <h6 className="mb-0">Events</h6>
                                <p className="small mb-0">
                                    Notifications about upcoming games, social events,
                                    tournaments, and special league activities.
                                </p>
                            </div>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="eventsSwitch"
                                    checked={localPreferences.events}
                                    onChange={() => handleToggle('events')}
                                />
                            </div>
                        </li>
                    </ul>
                    {/* Notification END */}
                </div>
                {/* Card body END */}

                {/* Save message */}
                {saveMessage && (
                    <div className="card-body pt-0">
                        <div className={`alert ${saveMessage.includes('successfully') ? 'alert-success' : 'alert-danger'} alert-sm mb-0`}>
                            {saveMessage}
                        </div>
                    </div>
                )}

                {/* Button save */}
                <div className="card-footer pt-0 text-end border-0">
                    <button
                        type="button"
                        className="btn btn-sm btn-primary mb-0"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            'Save changes'
                        )}
                    </button>
                </div>
            </div>
        </>
    );
}

export default NotificationTab;